import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip, IconButton, Menu, MenuItem, Tooltip, FormControl, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, Popover, Button, InputAdornment, Divider, useMediaQuery, useTheme, Drawer, Skeleton, LinearProgress } from '@mui/material';
import { Icon } from '@iconify/react';
export interface Column<T> {
  id: string;
  label: string;
  renderCell?: (row: T) => React.ReactNode;
  renderFilter?: () => React.ReactNode;
  filterValue?: any;
  onFilterChange?: (value: any) => void;
  sortable?: boolean;
}
export interface RowAction<T> {
  icon: string;
  label: string;
  onClick: (row: T) => void;
  condition?: (row: T) => boolean;
}
export interface FilterChip {
  id: string;
  label: string;
  value: string | number | boolean;
  onDelete: () => void;
}
export interface BulkAction<T> {
  icon: string;
  label: string;
  onClick: (selectedRows: T[]) => void;
  color?: 'primary' | 'error' | 'warning' | 'success' | 'info';
}
export interface DataTableProps<T> {
  title: string;
  data: T[];
  columns: Column<T>[];
  rowActions?: RowAction<T>[];
  keyField: keyof T;
  filterChips?: FilterChip[];
  onClearAllFilters?: () => void;
  loading?: boolean;
  initialVisibleColumns?: string[];
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchValue?: string;
  onExport?: () => void;
  emptyStateMessage?: string;
  rowsPerPageOptions?: number[];
  showColumnFilters?: boolean;
  onRowClick?: (row: T) => void;
  showSearch?: boolean;
  selectable?: boolean;
  selectedRows?: T[];
  onSelectionChange?: (selectedRows: T[]) => void;
  bulkActions?: BulkAction<T>[];
}
export function DataTable<T>({
  title,
  data,
  columns,
  rowActions,
  keyField,
  filterChips,
  onClearAllFilters,
  loading = false,
  initialVisibleColumns,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onExport,
  emptyStateMessage = 'No data found',
  rowsPerPageOptions = [10, 25, 50, 100],
  showColumnFilters = true,
  onRowClick,
  showSearch = false,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  bulkActions = []
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  // FIXED: Always include 'actions' in visible columns if rowActions exist
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const cols = initialVisibleColumns || columns.map(col => col.id);
    // Add 'actions' if rowActions exist and it's not already in the list
    if (rowActions && !cols.includes('actions')) {
      return [...cols, 'actions'];
    }
    return cols;
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [density, setDensity] = useState<'medium' | 'compact'>('medium');
  const [search, setSearch] = useState(searchValue);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: T) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const handleColumnsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnsMenuAnchor(event.currentTarget);
  };
  const handleColumnsMenuClose = () => {
    setColumnsMenuAnchor(null);
  };
  const handleColumnToggle = (columnId: string) => {
    if (visibleColumns.includes(columnId)) {
      if (visibleColumns.length > 1) {
        setVisibleColumns(visibleColumns.filter(col => col !== columnId));
      }
    } else {
      setVisibleColumns([...visibleColumns, columnId]);
    }
  };
  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      setFilterDrawerOpen(true);
    } else {
      setFilterMenuAnchor(event.currentTarget);
    }
  };
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
    setFilterDrawerOpen(false);
  };
  const handleDensityChange = () => {
    setDensity(density === 'medium' ? 'compact' : 'medium');
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };
  const clearSearch = () => {
    setSearch('');
    if (onSearchChange) {
      onSearchChange('');
    }
  };
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;
    if (event.target.checked) {
      onSelectionChange(data);
      return;
    }
    onSelectionChange([]);
  };
  const handleRowSelect = (event: React.ChangeEvent<HTMLInputElement>, row: T) => {
    if (!onSelectionChange) return;
    const selectedIndex = selectedRows.findIndex(selected => selected[keyField] === row[keyField]);
    let newSelected: T[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedRows.slice(0, selectedIndex), selectedRows.slice(selectedIndex + 1));
    }
    onSelectionChange(newSelected);
  };
  const isSelected = (row: T) => selectedRows.findIndex(selected => selected[keyField] === row[keyField]) !== -1;
  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  if (loading) {
    return <Card elevation={0} sx={{
      borderRadius: 2,
      border: '1px solid #E5E7EB'
    }} data-id="element-409">
        <CardContent sx={{
        p: 0,
        position: 'relative'
      }} data-id="element-410">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          flexWrap: 'wrap',
          gap: 1
        }} data-id="element-411">
            <Skeleton variant="text" width={150} height={32} data-id="element-412" />
            <Box sx={{
            display: 'flex',
            gap: 1
          }} data-id="element-413">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} variant="circular" width={32} height={32} data-id="element-414" />)}
            </Box>
          </Box>
          <Divider data-id="element-415" />
          <LinearProgress sx={{
          height: 3,
          backgroundColor: '#E5E7EB',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#0052CC'
          }
        }} data-id="element-416" />
          <TableContainer data-id="element-417">
            <Table size={density} stickyHeader data-id="element-418">
              <TableHead data-id="element-419">
                <TableRow data-id="element-420">
                  {selectable && <TableCell padding="checkbox" data-id="element-421">
                      <Skeleton variant="rectangular" width={24} height={24} data-id="element-422" />
                    </TableCell>}
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} sx={{
                  backgroundColor: '#F3F5F7',
                  fontWeight: 700,
                  color: '#0052CC'
                }} data-id="element-423">
                          <Skeleton variant="text" width="80%" height={24} data-id="element-424" />
                        </TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody data-id="element-425">
                {[...Array(5)].map((_, index) => <TableRow key={index} data-id="element-426">
                    {selectable && <TableCell padding="checkbox" data-id="element-427">
                        <Skeleton variant="rectangular" width={24} height={24} data-id="element-428" />
                      </TableCell>}
                    {visibleColumns.map(column => <TableCell key={column} data-id="element-429">
                        <Skeleton variant="text" width="90%" height={24} data-id="element-430" />
                      </TableCell>)}
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider data-id="element-431" />
          <Box p={2} data-id="element-432">
            <Skeleton variant="rectangular" width="100%" height={52} data-id="element-433" />
          </Box>
        </CardContent>
      </Card>;
  }
  return <Card elevation={0} sx={{
    borderRadius: 2,
    border: '1px solid #E5E7EB',
    maxWidth: '100%',
    overflow: 'hidden'
  }} data-id="element-434">
      <CardContent sx={{
      p: 0,
      position: 'relative',
      maxWidth: '100%'
    }} data-id="element-435">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 2,
        flexWrap: 'wrap',
        gap: 1
      }} data-id="element-436">
          {selectedRows.length > 0 ? <Typography variant="subtitle1" sx={{
          fontWeight: 600,
          color: '#0052CC'
        }} data-id="element-437">
              {selectedRows.length} selected
            </Typography> : <Typography variant="subtitle1" sx={{
          fontWeight: 500
        }} data-id="element-438">
              {title} ({data.length})
            </Typography>}
          <Box sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center'
        }} data-id="element-439">
            {selectedRows.length > 0 && bulkActions.length > 0 && <Box sx={{
            display: 'flex',
            gap: 1,
            mr: 2
          }} data-id="element-440">
                {bulkActions.map((action, index) => <Button key={index} variant="outlined" size="small" startIcon={<Icon icon={action.icon} data-id="element-442" />} onClick={() => action.onClick(selectedRows)} color={action.color || 'primary'} sx={{
              textTransform: 'none',
              fontWeight: 600
            }} data-id="element-441">
                    {action.label}
                  </Button>)}
              </Box>}
            <Tooltip title="Columns" data-id="element-443">
              <IconButton size="small" onClick={handleColumnsMenuOpen} data-id="element-444">
                <Icon icon="lucide:columns" width={18} height={18} data-id="element-445" />
              </IconButton>
            </Tooltip>
            {onExport && <Tooltip title="Export" data-id="element-446">
                <IconButton size="small" onClick={onExport} data-id="element-447">
                  <Icon icon="lucide:download" width={18} height={18} data-id="element-448" />
                </IconButton>
              </Tooltip>}
          </Box>
        </Box>
        {/* Filter chips */}
        {filterChips && filterChips.length > 0 && <Box sx={{
        px: 2,
        pb: 2,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1
      }} data-id="element-449">
            {filterChips.map(chip => <Chip key={chip.id} label={`${chip.label}: ${chip.value}`} onDelete={chip.onDelete} size="small" data-id="element-450" />)}
            {onClearAllFilters && <Chip label="Clear all" onClick={onClearAllFilters} size="small" variant="outlined" data-id="element-451" />}
          </Box>}
        <Divider data-id="element-452" />
        <TableContainer sx={{
        maxHeight: 'calc(100vh - 280px)',
        overflowX: 'auto'
      }} data-id="element-453">
          <Table size={density} stickyHeader data-id="element-454">
            <TableHead data-id="element-455">
              {showColumnFilters && <TableRow data-id="element-456">
                  {selectable && <TableCell sx={{
                backgroundColor: '#F8F9FA',
                borderBottom: 'none',
                p: 1
              }} data-id="element-457" />}
                  {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={`filter-${column.id}`} sx={{
                backgroundColor: '#F3F5F7',
                padding: '8px 16px',
                borderBottom: 'none',
                verticalAlign: 'top'
              }} data-id="element-458">
                          {column.renderFilter && column.renderFilter()}
                        </TableCell>)}
                  {rowActions && visibleColumns.includes('actions') && <TableCell sx={{
                backgroundColor: '#F3F5F7',
                padding: '8px 16px',
                borderBottom: 'none'
              }} data-id="element-459" />}
                </TableRow>}
              <TableRow data-id="element-460">
                {selectable && <TableCell padding="checkbox" sx={{
                backgroundColor: '#F3F5F7',
                borderBottom: '1px solid #E5E7EB'
              }} data-id="element-461">
                    <Checkbox color="primary" indeterminate={selectedRows.length > 0 && selectedRows.length < data.length} checked={data.length > 0 && selectedRows.length === data.length} onChange={handleSelectAllClick} size="small" data-id="element-462" />
                  </TableCell>}
                {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} sx={{
                backgroundColor: '#F3F5F7',
                fontWeight: 700,
                color: '#0052CC',
                transition: 'none'
              }} data-id="element-463">
                        {column.label}
                      </TableCell>)}
                {rowActions && visibleColumns.includes('actions') && <TableCell sx={{
                backgroundColor: '#F3F5F7',
                fontWeight: 700,
                color: '#0052CC',
                transition: 'none'
              }} data-id="element-464">
                    Actions
                  </TableCell>}
              </TableRow>
            </TableHead>
            <TableBody data-id="element-465">
              {paginatedData.length === 0 ? <TableRow data-id="element-466">
                  <TableCell colSpan={visibleColumns.length + (selectable ? 1 : 0)} align="center" sx={{
                py: 8
              }} data-id="element-467">
                    {emptyStateMessage}
                  </TableCell>
                </TableRow> : paginatedData.map((row, index) => {
              const isItemSelected = isSelected(row);
              return <TableRow key={String(row[keyField]) || index} hover selected={isItemSelected} onClick={() => onRowClick && onRowClick(row)} sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                '&:last-child td, &:last-child th': {
                  border: 0
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 82, 204, 0.04)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 82, 204, 0.08)'
                  }
                }
              }} data-id="element-468">
                      {selectable && <TableCell padding="checkbox" data-id="element-469">
                          <Checkbox color="primary" checked={isItemSelected} onChange={event => handleRowSelect(event, row)} onClick={event => event.stopPropagation()} size="small" data-id="element-470" />
                        </TableCell>}
                      {columns.map(column => visibleColumns.includes(column.id) && <TableCell key={column.id} data-id="element-471">
                              {column.renderCell ? column.renderCell(row) : String(row[column.id as keyof T] || '')}
                            </TableCell>)}
                      {rowActions && visibleColumns.includes('actions') && <TableCell data-id="element-472">
                          <IconButton size="small" onClick={event => {
                    event.stopPropagation();
                    handleMenuClick(event, row);
                  }} sx={{
                    color: '#6B7280',
                    '&:hover': {
                      backgroundColor: '#F0F3F7'
                    }
                  }} data-id="element-473">
                            <Icon icon="lucide:more-vertical" width={18} height={18} data-id="element-474" />
                          </IconButton>
                        </TableCell>}
                    </TableRow>;
            })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={rowsPerPageOptions} component="div" count={data.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} data-id="element-475" />
      </CardContent>
      {/* Row actions menu */}
      {rowActions && <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{
      elevation: 3,
      sx: {
        minWidth: 180,
        borderRadius: 2,
        mt: 0.5
      }
    }} data-id="element-476">
          {selectedRow && rowActions.filter(action => !action.condition || action.condition(selectedRow)).map((action, index) => <MenuItem key={index} onClick={event => {
        event.stopPropagation();
        action.onClick(selectedRow);
        handleMenuClose();
      }} sx={{
        py: 1.5
      }} data-id="element-477">
                  <Icon icon={action.icon} width={18} style={{
          marginRight: 12,
          color: '#64748B'
        }} data-id="element-478" />
                  <Typography variant="body2" fontWeight={500} data-id="element-479">
                    {action.label}
                  </Typography>
                </MenuItem>)}
        </Menu>}
      {/* Column visibility popover */}
      <Popover open={Boolean(columnsMenuAnchor)} anchorEl={columnsMenuAnchor} onClose={handleColumnsMenuClose} anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }} transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }} data-id="element-480">
        <Box sx={{
        p: 2,
        width: 200
      }} data-id="element-481">
          <Typography variant="subtitle2" sx={{
          mb: 1
        }} data-id="element-482">
            Show Columns
          </Typography>
          {columns.map(column => <Box key={column.id} sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.5
        }} data-id="element-483">
              <Checkbox checked={visibleColumns.includes(column.id)} onChange={() => handleColumnToggle(column.id)} size="small" disabled={visibleColumns.length === 1 && visibleColumns.includes(column.id)} data-id="element-484" />
              <Typography variant="body2" data-id="element-485">{column.label}</Typography>
            </Box>)}
          {rowActions && <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 0.5
        }} data-id="element-486">
              <Checkbox checked={visibleColumns.includes('actions')} onChange={() => handleColumnToggle('actions')} size="small" disabled={visibleColumns.length === 1 && visibleColumns.includes('actions')} data-id="element-487" />
              <Typography variant="body2" data-id="element-488">Actions</Typography>
            </Box>}
        </Box>
      </Popover>
    </Card>;
}
export default DataTable;